import BlobImage from 'components/blobImage';
import PageHeader from 'components/general/pageHeader';
import Bullet from 'components/prism/bullet';
import Subheader from 'components/prism/subheader';
import { ArrayHelpers, Loading, SystemText, StringHelpers } from '@salesperformanceint/e4shared';
import { isPast, isToday, isFuture } from 'helpers/dates';
import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { apiGet, apiPost } from 'services';
import Schedule from 'views/user/reviewTracker/components/schedule';
import ManageTask from 'views/user/components/manageTask';
import { loadPrismMenuStats, setShowSuccess } from 'actions/ui';

export const MyTeam = ({ match }) => {
    const dispatch = useDispatch();
    const [data, setData] = React.useState({});
    const [loading, setLoading] = React.useState(true);
    const [profileId, setProfileId] = React.useState(null);
    const [showSchedule, setShowSchedule] = React.useState(false);
    const [showSetObjective, setShowSetObjective] = React.useState(false);
    const [users, setUsers] = React.useState([]);
    const { user, downline } = useSelector(state => state.user);
    const { prismMultiColumn } = useSelector(state => state.ui);

    React.useEffect(() => {
        const urlId = match.params.id ? parseInt(match.params.id, 10) : null;

        // If no id in URL or id matches current user's id, use current user's id
        if (!urlId || urlId === user.id) {
            setProfileId(user.id);
        } else {
            // If id from URL doesn't match current user's id, use the URL id

            setProfileId(urlId);
        }
    }, [match.params.id, user.id]);

    // Process and set team data
    const processTeamData = React.useCallback((response, isInitialLoad = false) => {
        const sortedReports = response.reports
            ? [...response.reports].sort((a, b) => {
                  const nameA = (a.full_Name || '').toLowerCase();
                  const nameB = (b.full_Name || '').toLowerCase();
                  return nameA.localeCompare(nameB);
              })
            : [];

        const totalReports = response.reports?.reduce((sum, report) => sum + (report.totalDownline || 0), 0) || 0;

        setData({
            ...response,
            reports: sortedReports,
            totalReports,
        });

        if (isInitialLoad) {
            setLoading(false);
        }
    }, []);

    // Reload team data
    const reloadTeamData = React.useCallback(() => {
        if (profileId) {
            apiGet(`myteam/${profileId}`, user.guid, response => {
                processTeamData(response);
            });
        }
    }, [profileId, user.guid, processTeamData]);

    React.useEffect(() => {
        if (profileId) {
            apiGet(`myteam/${profileId}`, user.guid, response => {
                processTeamData(response, true);
            });
        }
    }, [profileId, user.guid, processTeamData]);

    // Load review users for the Schedule modal
    React.useEffect(() => {
        apiGet('review-users', user.guid, response => {
            setUsers(response);
        });
    }, [user.guid]);

    // Handle scheduling reviews
    const handleSchedule = (details, callback) => {
        apiPost('review-assign', details, user.guid, () => {
            dispatch(setShowSuccess(SystemText('ReviewTracker/Tabs/Reviews/Success')));
            setShowSchedule(false);
            callback();
            reloadTeamData();
        });
    };

    const extra = (
        <div className="">
            <button className="btn btn-primary btn-cornered mr5" onClick={() => setShowSchedule(true)}>
                {SystemText('MyTeam/Button/ScheduleReview')}
            </button>
            <button className="btn btn-primary btn-cornered" onClick={() => setShowSetObjective(true)}>
                {SystemText('MyTeam/Button/SetObjective')}
            </button>
        </div>
    );

    return (
        <div>
            <PageHeader title={SystemText('MyTeam/Heading/MyTeam')} hideCrumb />
            <section className="content" data-testid="teams-content">
                {loading ? (
                    <Loading />
                ) : (
                    <>
                        <Subheader
                            title={(() => {
                                if (profileId !== user.id) {
                                    const profile = downline?.find(p => p.id === profileId);
                                    if (profile?.first_Name) {
                                        return `${StringHelpers.possession(profile.first_Name)} Team Members`;
                                    }
                                }
                                return SystemText('MyTeam/Heading/TeamMembers');
                            })()}
                            count={data.totalReports}
                            extra={extra}
                        />

                        <div className={prismMultiColumn ? 'even-col' : ''}>
                            {data.reports?.map(item => {
                                const reviewDatesCount = ArrayHelpers.existsAndHasLength(item.review_Dates)
                                    ? item.review_Dates.filter(date => isPast(date) || isToday(date) || isFuture(date))
                                          .length
                                    : 0;

                                const overdueObjectivesCount = ArrayHelpers.existsAndHasLength(item.objective_Dates)
                                    ? item.objective_Dates.filter(date => isPast(date) && !isToday(date)).length
                                    : 0;

                                const overduePathwaysCount = ArrayHelpers.existsAndHasLength(item.pathway_Dates)
                                    ? item.pathway_Dates.filter(date => isPast(date) && !isToday(date)).length
                                    : 0;

                                const objectivesAwaitingApprovalCount = item.objectives_Awaiting_Approval || 0;

                                const actionsCount = reviewDatesCount + objectivesAwaitingApprovalCount;

                                const allCaughtUp =
                                    overdueObjectivesCount === 0 && overduePathwaysCount === 0 && reviewDatesCount === 0;

                                return (
                                    <Link
                                        key={`team-${item.id}`}
                                        to={`/team-member/${item.id}`}
                                        className={`prism-content-box anchor-top ${prismMultiColumn ? '' : 'mb10'}`}
                                    >
                                        <div className="prism-content flex flex-column">
                                            <div className="flex">
                                                <BlobImage
                                                    className="img-circle"
                                                    width="50"
                                                    height="50"
                                                    folder="avatar"
                                                    src={item.avatar}
                                                    initials={item.initials}
                                                />
                                                <div className="ml10">
                                                    <h3>{item.full_Name}</h3>
                                                    <div className="flex flex-center mt10">
                                                        {item.job_Title && (
                                                            <span className="flex-grow">{item.job_Title}</span>
                                                        )}
                                                        {item.staff > 0 && (
                                                            <>
                                                                <i className="fa fa-users mr10 ml10" />
                                                                <span>{item.staff}</span>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <hr className="mt10" />
                                            <div className="mt10 flex flex-column flex-grow">
                                                {actionsCount > 0 && (
                                                    <div className="flex flex-center mb5">
                                                        <span className="badge label-danger mr10">{actionsCount}</span>
                                                        <span>{SystemText('MyTeam/Message/ActionsToComplete')}</span>
                                                    </div>
                                                )}
                                                {ArrayHelpers.existsAndHasLength(item.review_Dates) &&
                                                    (() => {
                                                        const hasOverdue = item.review_Dates.some(
                                                            date => isPast(date) && !isToday(date)
                                                        );
                                                        const hasDueToday = item.review_Dates.some(date => isToday(date));
                                                        const hasFuture = item.review_Dates.some(date => isFuture(date));

                                                        if (hasOverdue) {
                                                            return (
                                                                <div className="mb5">
                                                                    <Bullet
                                                                        icon="circle-exclamation"
                                                                        style="overdue"
                                                                        value={SystemText('MyTeam/ReviewComments/Overdue')}
                                                                    />
                                                                </div>
                                                            );
                                                        }

                                                        if (hasDueToday) {
                                                            return (
                                                                <div className="mb5">
                                                                    <Bullet
                                                                        icon="triangle-exclamation"
                                                                        style="due"
                                                                        value={SystemText('MyTeam/ReviewComments/DueToday')}
                                                                    />
                                                                </div>
                                                            );
                                                        }

                                                        if (hasFuture) {
                                                            return (
                                                                <div className="mb5">
                                                                    <Bullet
                                                                        icon="calendar"
                                                                        style="steel"
                                                                        value={SystemText('MyTeam/ReviewComments/Due')}
                                                                    />
                                                                </div>
                                                            );
                                                        }

                                                        return null;
                                                    })()}
                                                {objectivesAwaitingApprovalCount > 0 && (
                                                    <div className="mb5">
                                                        <Bullet
                                                            icon="spinner"
                                                            style="amber"
                                                            value={`${objectivesAwaitingApprovalCount} ${SystemText('MyTeam/Message/ObjectivesAwaitingApproval')}`}
                                                        />
                                                    </div>
                                                )}
                                                {overduePathwaysCount > 0 && (
                                                    <div className="mb5">
                                                        <Bullet
                                                            icon="circle-exclamation"
                                                            style="overdue"
                                                            value={`${overduePathwaysCount} Pathways overdue`}
                                                        />
                                                    </div>
                                                )}
                                                {overdueObjectivesCount > 0 && (
                                                    <div className="mb5">
                                                        <Bullet
                                                            icon="circle-exclamation"
                                                            style="overdue"
                                                            value={`${overdueObjectivesCount} Objectives overdue`}
                                                        />
                                                    </div>
                                                )}
                                                {allCaughtUp && (
                                                    <div className="flex flex-column flex-center">
                                                        <i className="fa fa-star" />
                                                        <span className="mt10">
                                                            {SystemText('MyTeam/Message/AllCaughtUp')}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex gap-1">
                                                <button className="btn btn-primary btn-cornered">
                                                    {SystemText('MyTeam/Button/ViewProfile')}
                                                </button>
                                                {item.staff > 0 && (
                                                    <Link
                                                        to={`/my-team/${item.id}`}
                                                        className="btn btn-tertiary btn-cornered"
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                        }}
                                                    >
                                                        {SystemText('MyTeam/Button/ViewTeam')}
                                                    </Link>
                                                )}
                                            </div>
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                    </>
                )}
            </section>
            <Schedule
                show={showSchedule}
                users={users}
                close={() => {
                    setShowSchedule(false);
                    dispatch(loadPrismMenuStats());
                }}
                onSchedule={handleSchedule}
            />
            <ManageTask
                values={{ owner_Id: user.id }}
                show={showSetObjective}
                reload={reloadTeamData}
                close={() => setShowSetObjective(false)}
            />
        </div>
    );
};

export default MyTeam;
